import React, { useMemo, useState } from "react";
import { BsChevronDown, BsEye, BsEyeSlash, BsSearch } from "react-icons/bs";
import { IoCloseCircle } from "react-icons/io5";
import { netural400, neturalBlack, primaryGrey } from "../../constants/color";

/**
 * Theme constants import
 * Update path based on your project structure
 */

const STATUS = {
  NORMAL: "normal",
  ERROR: "error",
  SUCCESS: "success",
  WARNING: "warning",
};

const SIZE_CONFIG = {
  small: {
    padding: "8px 12px",
    fontSize: "13px",
    minHeight: "40px",
    iconSize: 16,
  },
  medium: {
    padding: "10px 14px",
    fontSize: "14px",
    minHeight: "46px",
    iconSize: 18,
  },
  large: {
    padding: "12px 16px",
    fontSize: "15px",
    minHeight: "52px",
    iconSize: 20,
  },
};

const STATUS_COLOR = {
  normal: primaryGrey,
  error: "#EA1010",
  success: "#16A34A",
  warning: "#F59E0B",
};

const spinnerStyle = {
  width: 16,
  height: 16,
  border: "2px solid #E5E5E5",
  borderTop: "2px solid #666",
  borderRadius: "50%",
  animation: "spin 0.8s linear infinite",
};

const AppInput = ({
  /* Core */
  type = "text", // text | search | display | password | number | textarea
  value,
  defaultValue = "",
  onChange,
  onClick,

  /* Content */
  label,
  placeholder = "Enter value",
  helperText,
  required = false,

  /* State */
  disabled = false,
  readOnly = false,
  loading = false,
  clearable = false,
  showDropdown = false,

  /* Validation */
  status = STATUS.NORMAL,

  /* Size */
  size = "medium",

  /* Icons */
  leftIcon,
  rightIcon,

  /* Addons */
  prefix,
  suffix,

  /* Display */
  fullWidth = true,
  rows = 4,

  /* Style */
  containerStyle = {},
  inputStyle = {},
  labelStyle = {},
  helperStyle = {},

  /* Callbacks */
  onClear,

  ...props
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [showPassword, setShowPassword] = useState(false);

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  const config = SIZE_CONFIG[size] || SIZE_CONFIG.medium;
  const borderColor = STATUS_COLOR[status] || STATUS_COLOR.normal;

  const isClickableDisplay = type === "display";
  const isTextarea = type === "textarea";
  const isPassword = type === "password";
  const isSearch = type === "search";

  const resolvedInputType = useMemo(() => {
    if (isClickableDisplay) return "text";
    if (isPassword) return showPassword ? "text" : "password";
    return type;
  }, [type, isClickableDisplay, isPassword, showPassword]);

  const hasValue =
    currentValue !== undefined &&
    currentValue !== null &&
    String(currentValue).trim() !== "";

  const handleChange = (e) => {
    if (disabled || readOnly || isClickableDisplay) return;

    let newValue = e.target.value;

    // Number validation
    if (type === "number") {
      // allow empty value
      if (newValue !== "") {
        const numericValue = Number(newValue);

        // invalid number
        if (Number.isNaN(numericValue)) return;

        // min restriction
        if (props.min !== undefined && numericValue < props.min) {
          newValue = String(props.min);
        }

        // max restriction
        if (props.max !== undefined && numericValue > props.max) {
          newValue = String(props.max);
        }
      }
    }

    const updatedEvent = {
      ...e,
      target: {
        ...e.target,
        value: newValue,
      },
    };

    if (!isControlled) {
      setInternalValue(newValue);
    }

    onChange?.(updatedEvent);
  };

  const handleClear = (e) => {
    e.stopPropagation();

    if (!isControlled) {
      setInternalValue("");
    }

    onClear?.();

    if (onChange) {
      onChange({
        target: {
          value: "",
        },
      });
    }
  };

  const renderLeft = () => {
    if (leftIcon) return leftIcon;
    if (isSearch) return <BsSearch color={netural400} />;
    return null;
  };

  const renderRight = () => {
    if (loading) {
      return <div style={spinnerStyle} />;
    }

    if (clearable && hasValue && !disabled) {
      return (
        <IoCloseCircle
          size={config.iconSize}
          color={netural400}
          style={{ cursor: "pointer", flexShrink: 0 }}
          onClick={handleClear}
        />
      );
    }

    if (isPassword) {
      return showPassword ? (
        <BsEyeSlash
          size={config.iconSize}
          color={netural400}
          style={{ cursor: "pointer", flexShrink: 0 }}
          onClick={() => setShowPassword(false)}
        />
      ) : (
        <BsEye
          size={config.iconSize}
          color={netural400}
          style={{ cursor: "pointer", flexShrink: 0 }}
          onClick={() => setShowPassword(true)}
        />
      );
    }

    if (rightIcon) return rightIcon;

    if (showDropdown) {
      return (
        <BsChevronDown
          size={config.iconSize}
          color={netural400}
          style={{ flexShrink: 0 }}
        />
      );
    }

    return null;
  };

  const WrapperTag = isTextarea ? "textarea" : "input";

  return (
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>

      <div
        style={{
          width: fullWidth ? "100%" : "auto",
          ...containerStyle,
        }}
      >
        {label && (
          <label
            style={{
              display: "block",
              marginBottom: "6px",
              fontSize: "14px",
              color: netural400,
              fontWeight: 400,
              ...labelStyle,
            }}
          >
            {label}
            {required && (
              <span style={{ color: "#EA1010", marginLeft: 4 }}>*</span>
            )}
          </label>
        )}

        <div
          onClick={() => {
            if (!disabled) onClick?.();
          }}
          style={{
            display: "flex",
            alignItems: isTextarea ? "flex-start" : "center",
            gap: type === "number" ? "0px" : "8px",
            border: `1px solid ${borderColor}`,
            borderRadius: "12px",
            padding: config.padding,
            minHeight: isTextarea ? undefined : config.minHeight,
            backgroundColor: disabled ? "#f9f9f9" : "#fff",
            cursor: disabled
              ? "default"
              : isClickableDisplay
                ? "pointer"
                : "text",
            transition: "all 0.2s ease",
          }}
        >
          {prefix && <div>{prefix}</div>}

          {renderLeft() && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              {renderLeft()}
            </div>
          )}

          <WrapperTag
            type={isTextarea ? undefined : resolvedInputType}
            value={currentValue}
            onChange={handleChange}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly || isClickableDisplay}
            rows={isTextarea ? rows : undefined}
            autoComplete="off"
            style={{
              flex: 1,
              width: "100%",
              border: "none",
              outline: "none",
              resize: "none",
              background: "transparent",
              fontSize: config.fontSize,
              color: hasValue ? neturalBlack : netural400,
              fontFamily: "inherit",
              cursor: disabled
                ? "default"
                : isClickableDisplay
                  ? "pointer"
                  : "text",
              ...inputStyle,
            }}
            {...props}
          />

          {suffix && <div>{suffix}</div>}

          {renderRight() && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              {renderRight()}
            </div>
          )}
        </div>

        {helperText && (
          <label
            style={{
              display: "block",
              marginTop: "4px",
              fontSize: "12px",
              color: borderColor,
              ...helperStyle,
            }}
          >
            {helperText}
          </label>
        )}
      </div>
    </>
  );
};

export default AppInput;
